// product/realm.schema.ts
import { z } from "zod";
var ProductRealmSchema = z.enum(["cannabis", "hemp", "general"]);
var PRODUCT_REALMS = ProductRealmSchema.options;
var RealmComplianceConfigSchema = z.object({
  /** State-level tracking system (Metrc for cannabis, none for others). */
  trackingSystem: z.enum(["metrc", "none"]).default("none"),
  /** Whether orders require a licensed delivery destination. */
  requiresLicensedDestination: z.boolean().default(false),
  /** Whether shipments require manifest preparation before transport. */
  requiresManifest: z.boolean().default(false),
  /**
   * Document types recommended for products in this realm.
   * Informational — drives UI hints, not enforcement.
   */
  recommendedDocumentTypes: z.array(z.string()).default([])
});
var REALM_COMPLIANCE_DEFAULTS = {
  cannabis: {
    trackingSystem: "metrc",
    requiresLicensedDestination: true,
    requiresManifest: true,
    recommendedDocumentTypes: ["COA", "Lab Report", "Product Label"]
  },
  hemp: {
    trackingSystem: "none",
    requiresLicensedDestination: false,
    requiresManifest: false,
    recommendedDocumentTypes: ["COA", "Lab Report", "Hemp Processor License"]
  },
  general: {
    trackingSystem: "none",
    requiresLicensedDestination: false,
    requiresManifest: false,
    recommendedDocumentTypes: ["COA", "Food Safety Certificate", "Supplement Facts Panel"]
  }
};
var REALM_FULFILLMENT_DEFAULTS = {
  cannabis: { fulfillmentMethod: "local_delivery", defaultSpreadRate: 0.1 },
  hemp: { fulfillmentMethod: "mail_carrier", defaultSpreadRate: 0.04 },
  general: { fulfillmentMethod: "mail_carrier", defaultSpreadRate: 0.04 }
};
function resolveOrgBrandFulfillment(orgBrandFulfillmentMethod, orgBrandSpreadRate, brandRealm) {
  const defaults = REALM_FULFILLMENT_DEFAULTS[brandRealm];
  return {
    fulfillmentMethod: orgBrandFulfillmentMethod ?? defaults.fulfillmentMethod,
    spreadRate: orgBrandSpreadRate ?? defaults.defaultSpreadRate
  };
}
function realmRequiresCompliance(realm) {
  return REALM_COMPLIANCE_DEFAULTS[realm].trackingSystem === "metrc";
}
var RealmContextSchema = z.object({
  realm: ProductRealmSchema,
  source: z.enum(["brand", "default"]),
  compliance: RealmComplianceConfigSchema
});
var GENERAL_MERCHANDISE_CATEGORIES = [
  {
    name: "Beverages",
    handle: "gm-beverages",
    children: [
      { name: "Functional Drinks", handle: "gm-functional-drinks" },
      { name: "Sparkling Water", handle: "gm-sparkling-water" },
      { name: "Juice & Smoothies", handle: "gm-juice-smoothies" },
      { name: "Tea & Coffee", handle: "gm-tea-coffee" },
      { name: "Mixers & Mocktails", handle: "gm-mixers-mocktails" }
    ]
  },
  {
    name: "Snacks & Confections",
    handle: "gm-snacks-confections",
    children: [
      { name: "Mints & Gum", handle: "gm-mints-gum" },
      { name: "Chocolates & Candy", handle: "gm-chocolates-candy" },
      { name: "Chips & Savory Snacks", handle: "gm-chips-savory-snacks" },
      { name: "Jerky & Protein Snacks", handle: "gm-jerky-protein-snacks" }
    ]
  },
  {
    name: "Supplements",
    handle: "gm-supplements",
    children: [
      { name: "Capsules & Softgels", handle: "gm-capsules-softgels" },
      { name: "Powders & Drink Mixes", handle: "gm-powders-drink-mixes" },
      { name: "Tinctures & Drops", handle: "gm-tinctures-drops" },
      { name: "Gummies & Chewables", handle: "gm-gummies-chewables" }
    ]
  },
  {
    name: "Topicals & Personal Care",
    handle: "gm-topicals-personal-care",
    children: [
      { name: "Lotions & Creams", handle: "gm-lotions-creams" },
      { name: "Balms & Salves", handle: "gm-balms-salves" },
      { name: "Bath & Body", handle: "gm-bath-body" },
      { name: "Lip Care", handle: "gm-lip-care" }
    ]
  },
  {
    name: "Accessories",
    handle: "gm-accessories",
    children: [
      { name: "Apparel & Merch", handle: "gm-apparel-merch" },
      { name: "Storage & Containers", handle: "gm-storage-containers" },
      { name: "Lifestyle & Gifts", handle: "gm-lifestyle-gifts" }
    ]
  },
  {
    name: "Pet Products",
    handle: "gm-pet-products",
    children: [
      { name: "Pet Supplements", handle: "gm-pet-supplements" },
      { name: "Pet Treats", handle: "gm-pet-treats" }
    ]
  }
];

export {
  ProductRealmSchema,
  PRODUCT_REALMS,
  RealmComplianceConfigSchema,
  REALM_COMPLIANCE_DEFAULTS,
  REALM_FULFILLMENT_DEFAULTS,
  resolveOrgBrandFulfillment,
  realmRequiresCompliance,
  RealmContextSchema,
  GENERAL_MERCHANDISE_CATEGORIES
};
